package com.nonopn.skydome.common.app.pages.keraunic

import kotlin.math.PI
import kotlin.math.asin
import kotlin.math.cos
import kotlin.math.sin
import kotlin.math.sqrt

// Reference cities used to compare a site Nsg with nearby significant cities
object KeraunicCities {
    data class City(val name: String, val lat: Double, val lon: Double)

    private const val EARTH_RADIUS_KM = 6371.0

    // Returns the closest reference cities, nearest first
    fun nearest(lat: Double, lon: Double, count: Int): List<City> =
        cities.sortedBy { distanceKm(lat, lon, it.lat, it.lon) }.take(count)

    private fun distanceKm(lat1: Double, lon1: Double, lat2: Double, lon2: Double): Double {
        val dLat = (lat2 - lat1) * PI / 180.0
        val dLon = (lon2 - lon1) * PI / 180.0
        val a = sin(dLat / 2) * sin(dLat / 2) +
            cos(lat1 * PI / 180.0) * cos(lat2 * PI / 180.0) * sin(dLon / 2) * sin(dLon / 2)
        return 2 * EARTH_RADIUS_KM * asin(sqrt(a))
    }

    private val cities = listOf(
        City("Paris", 48.8566, 2.3522),
        City("Lyon", 45.764, 4.8357),
        City("Marseille", 43.2965, 5.3698),
        City("Bordeaux", 44.8378, -0.5792),
        City("Toulouse", 43.6047, 1.4442),
        City("Nantes", 47.2184, -1.5536),
        City("Strasbourg", 48.5734, 7.7521),
        City("Lille", 50.6292, 3.0573),
        City("Montpellier", 43.6108, 3.8767),
        City("Nice", 43.7102, 7.262),
        City("Ajaccio", 41.9192, 8.7386),
        City("Saint-Denis (La Réunion)", -20.8789, 55.4481),
        City("Fort-de-France", 14.6161, -61.0588),
        City("Pointe-à-Pitre", 16.2411, -61.5331),
        City("Cayenne", 4.9224, -52.3135),
        City("Nouméa", -22.2758, 166.458),
        City("Papeete", -17.5516, -149.5585),
        City("Londres", 51.5072, -0.1276),
        City("Dublin", 53.3498, -6.2603),
        City("Madrid", 40.4168, -3.7038),
        City("Barcelone", 41.3874, 2.1686),
        City("Séville", 37.3891, -5.9845),
        City("Lisbonne", 38.7223, -9.1393),
        City("Porto", 41.1579, -8.6291),
        City("Rome", 41.9028, 12.4964),
        City("Milan", 45.4642, 9.19),
        City("Naples", 40.8518, 14.2681),
        City("Palerme", 38.1157, 13.3615),
        City("La Valette", 35.8989, 14.5146),
        City("Athènes", 37.9838, 23.7275),
        City("Istanbul", 41.0082, 28.9784),
        City("Ankara", 39.9334, 32.8597),
        City("Berlin", 52.52, 13.405),
        City("Munich", 48.1351, 11.582),
        City("Francfort", 50.1109, 8.6821),
        City("Hambourg", 53.5511, 9.9937),
        City("Vienne", 48.2082, 16.3738),
        City("Zurich", 47.3769, 8.5417),
        City("Genève", 46.2044, 6.1432),
        City("Bruxelles", 50.8503, 4.3517),
        City("Amsterdam", 52.3676, 4.9041),
        City("Copenhague", 55.6761, 12.5683),
        City("Stockholm", 59.3293, 18.0686),
        City("Oslo", 59.9139, 10.7522),
        City("Helsinki", 60.1699, 24.9384),
        City("Varsovie", 52.2297, 21.0122),
        City("Prague", 50.0755, 14.4378),
        City("Budapest", 47.4979, 19.0402),
        City("Bucarest", 44.4268, 26.1025),
        City("Sofia", 42.6977, 23.3219),
        City("Belgrade", 44.7866, 20.4489),
        City("Zagreb", 45.815, 15.9819),
        City("Kiev", 50.4501, 30.5234),
        City("Moscou", 55.7558, 37.6173),
        City("Casablanca", 33.5731, -7.5898),
        City("Rabat", 34.0209, -6.8416),
        City("Marrakech", 31.6295, -7.9811),
        City("Alger", 36.7538, 3.0588),
        City("Tunis", 36.8065, 10.1815),
        City("Tripoli", 32.8872, 13.1913),
        City("Le Caire", 30.0444, 31.2357),
        City("Nouakchott", 18.0735, -15.9582),
        City("Dakar", 14.7167, -17.4677),
        City("Bamako", 12.6392, -8.0029),
        City("Ouagadougou", 12.3714, -1.5197),
        City("Niamey", 13.5116, 2.1254),
        City("Conakry", 9.6412, -13.5784),
        City("Freetown", 8.4657, -13.2317),
        City("Monrovia", 6.3156, -10.8074),
        City("Abidjan", 5.3599, -4.0083),
        City("Yamoussoukro", 6.8276, -5.2893),
        City("Accra", 5.6037, -0.187),
        City("Lomé", 6.1725, 1.2314),
        City("Cotonou", 6.3703, 2.3912),
        City("Lagos", 6.5244, 3.3792),
        City("Abuja", 9.0765, 7.3986),
        City("Yaoundé", 3.848, 11.5021),
        City("Douala", 4.0511, 9.7679),
        City("Libreville", 0.4162, 9.4673),
        City("Brazzaville", -4.2634, 15.2429),
        City("Kinshasa", -4.4419, 15.2663),
        City("Luanda", -8.839, 13.2894),
        City("N'Djamena", 12.1348, 15.0557),
        City("Khartoum", 15.5007, 32.5599),
        City("Addis-Abeba", 9.0192, 38.7525),
        City("Nairobi", -1.2921, 36.8219),
        City("Dar es Salaam", -6.7924, 39.2083),
        City("Kampala", 0.3476, 32.5825),
        City("Kigali", -1.9441, 30.0619),
        City("Lusaka", -15.3875, 28.3228),
        City("Harare", -17.8252, 31.0335),
        City("Maputo", -25.9692, 32.5732),
        City("Johannesburg", -26.2041, 28.0473),
        City("Le Cap", -33.9249, 18.4241),
        City("Antananarivo", -18.8792, 47.5079),
        City("Port-Louis", -20.1609, 57.5012),
        City("Djibouti", 11.5721, 43.1456),
        City("Tel Aviv", 32.0853, 34.7818),
        City("Beyrouth", 33.8938, 35.5018),
        City("Amman", 31.9454, 35.9284),
        City("Riyad", 24.7136, 46.6753),
        City("Djeddah", 21.4858, 39.1925),
        City("Dubaï", 25.2048, 55.2708),
        City("Doha", 25.2854, 51.531),
        City("Koweït", 29.3759, 47.9774),
        City("Téhéran", 35.6892, 51.389),
        City("Bagdad", 33.3152, 44.3661),
        City("Karachi", 24.8607, 67.0011),
        City("Lahore", 31.5204, 74.3587),
        City("Islamabad", 33.6844, 73.0479),
        City("New Delhi", 28.6139, 77.209),
        City("Mumbai", 19.076, 72.8777),
        City("Bangalore", 12.9716, 77.5946),
        City("Chennai", 13.0827, 80.2707),
        City("Calcutta", 22.5726, 88.3639),
        City("Colombo", 6.9271, 79.8612),
        City("Dacca", 23.8103, 90.4125),
        City("Katmandou", 27.7172, 85.324),
        City("Rangoun", 16.8409, 96.1735),
        City("Bangkok", 13.7563, 100.5018),
        City("Chiang Mai", 18.7883, 98.9853),
        City("Phuket", 7.8804, 98.3923),
        City("Phnom Penh", 11.5564, 104.9282),
        City("Siem Reap", 13.3633, 103.8564),
        City("Sihanoukville", 10.6275, 103.5222),
        City("Vientiane", 17.9757, 102.6331),
        City("Hanoï", 21.0278, 105.8342),
        City("Hô Chi Minh-Ville", 10.8231, 106.6297),
        City("Da Nang", 16.0544, 108.2022),
        City("Kuala Lumpur", 3.139, 101.6869),
        City("Singapour", 1.3521, 103.8198),
        City("Jakarta", -6.2088, 106.8456),
        City("Surabaya", -7.2575, 112.7521),
        City("Bali (Denpasar)", -8.6705, 115.2126),
        City("Manille", 14.5995, 120.9842),
        City("Cebu", 10.3157, 123.8854),
        City("Davao", 7.1907, 125.4553),
        City("Hong Kong", 22.3193, 114.1694),
        City("Canton", 23.1291, 113.2644),
        City("Shanghai", 31.2304, 121.4737),
        City("Pékin", 39.9042, 116.4074),
        City("Chengdu", 30.5728, 104.0668),
        City("Taipei", 25.033, 121.5654),
        City("Séoul", 37.5665, 126.978),
        City("Tokyo", 35.6762, 139.6503),
        City("Osaka", 34.6937, 135.5023),
        City("Oulan-Bator", 47.8864, 106.9057),
        City("Tachkent", 41.2995, 69.2401),
        City("Sydney", -33.8688, 151.2093),
        City("Melbourne", -37.8136, 144.9631),
        City("Brisbane", -27.4698, 153.0251),
        City("Perth", -31.9505, 115.8605),
        City("Darwin", -12.4634, 130.8456),
        City("Auckland", -36.8485, 174.7633),
        City("Port Moresby", -9.4438, 147.1803),
        City("New York", 40.7128, -74.006),
        City("Washington", 38.9072, -77.0369),
        City("Miami", 25.7617, -80.1918),
        City("Atlanta", 33.749, -84.388),
        City("Houston", 29.7604, -95.3698),
        City("Dallas", 32.7767, -96.797),
        City("Chicago", 41.8781, -87.6298),
        City("Denver", 39.7392, -104.9903),
        City("Los Angeles", 34.0522, -118.2437),
        City("San Francisco", 37.7749, -122.4194),
        City("Seattle", 47.6062, -122.3321),
        City("Montréal", 45.5017, -73.5673),
        City("Toronto", 43.6532, -79.3832),
        City("Vancouver", 49.2827, -123.1207),
        City("Mexico", 19.4326, -99.1332),
        City("Guatemala", 14.6349, -90.5069),
        City("San José (Costa Rica)", 9.9281, -84.0907),
        City("Panama", 8.9824, -79.5199),
        City("La Havane", 23.1136, -82.3666),
        City("Saint-Domingue", 18.4861, -69.9312),
        City("Port-au-Prince", 18.5944, -72.3074),
        City("Bogota", 4.711, -74.0721),
        City("Medellín", 6.2476, -75.5658),
        City("Caracas", 10.4806, -66.9036),
        City("Quito", -0.1807, -78.4678),
        City("Lima", -12.0464, -77.0428),
        City("La Paz", -16.4897, -68.1193),
        City("Santiago", -33.4489, -70.6693),
        City("Buenos Aires", -34.6037, -58.3816),
        City("Montevideo", -34.9011, -56.1645),
        City("Asunción", -25.2637, -57.5759),
        City("São Paulo", -23.5505, -46.6333),
        City("Rio de Janeiro", -22.9068, -43.1729),
        City("Brasilia", -15.7939, -47.8828),
        City("Salvador", -12.9777, -38.5016),
        City("Fortaleza", -3.7319, -38.5267),
        City("Manaus", -3.119, -60.0217),
        City("Recife", -8.0476, -34.877),
        City("Belém", -1.4558, -48.4902),
        City("Porto Alegre", -30.0346, -51.2177),
    )
}
